import { AnimateLayoutChanges, defaultAnimateLayoutChanges, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { twMerge } from 'tailwind-merge'
import { InstanceForPaginationType } from '../types/instance-interface'
import { getRowId } from '../utils'
import { TableRowsProps } from './table-rows'

export type TableRowProps<T extends InstanceForPaginationType<T>> = React.PropsWithChildren<
  Pick<
    TableRowsProps<T>,
    | 'data'
    | 'testAttributePostfix'
    | 'noHeader'
    | 'firstLineRect'
    | 'onRowClick'
    | 'nestedId'
    | 'paginationIdField'
    | 'rowIdParser'
    | 'withBorders'
    | 'isSortable'
    | 'className'
  > & {
    row: T
    index?: number
  } & Omit<React.HTMLProps<HTMLDivElement>, 'data' | 'className'>
>

const animateLayoutChanges: AnimateLayoutChanges = args =>
  defaultAnimateLayoutChanges({ ...args, wasDragging: true })

const TableRow = <T extends InstanceForPaginationType<T>>({
  data,
  testAttributePostfix,
  noHeader,
  firstLineRect,
  onRowClick,
  nestedId,
  paginationIdField,
  rowIdParser,
  withBorders,
  row,
  index,
  className,
  children,
  style,
  isSortable,
  ...props
}: TableRowProps<T>) => {
  const id = getRowId({ row, nestedId, paginationIdField, rowIdParser })
  const { newIndex, attributes, isDragging, listeners, setNodeRef, transform } = useSortable({
    id,
    animateLayoutChanges,
    disabled: !isSortable,
  })

  const rowIndex = newIndex !== -1 ? newIndex : index

  const dndStyle = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : undefined,
  }

  const receivedClassName = typeof className === 'string' ? className : className?.(row)

  return (
    <div
      ref={setNodeRef}
      style={{ ...dndStyle, ...style }}
      {...attributes}
      {...listeners}
      onClick={e => {
        const target = e.target as HTMLDivElement
        const button = target.closest('button')
        const link = target.closest('a')
        const modalBackground = target.closest('#modal-background')
        if (button || link || modalBackground) return
        if (onRowClick) {
          onRowClick(row)
        }
      }}
      className={twMerge(
        'hover:border-brand-primary main-transition-colors even:bg-brand-primary-subtle odd:bg-background-secondary flex flex-col items-start gap-4 border p-4 lg:flex-row lg:items-center lg:gap-0 lg:p-0',
        rowIndex === 0 &&
          !firstLineRect &&
          (noHeader ? 'rounded-t-lg' : 'rounded-t-lg lg:rounded-t-none'),
        rowIndex === (data?.items.length || 0) - 1 && 'rounded-b-lg',
        withBorders && 'border-b-text-primary/15 border last:border-b-transparent',
        isDragging ? ' border-brand-primary border-dashed' : 'border-transparent ',
        isSortable ? 'cursor-grab' : onRowClick ? 'cursor-pointer' : 'cursor-auto',
        receivedClassName,
      )}
      {...(testAttributePostfix
        ? {
            ['data-test-id']: `${testAttributePostfix}-item-${id}`,
          }
        : {})}
      {...props}
    >
      {children}
    </div>
  )
}

export default TableRow
